/**
 * The model for the LSCloud /Account/PaymentType/:id REST endpoint
 *
 * Copyright 2014 LightSpeed Retail. All rights reserved.
 * Created by kurtbergeron on 2014-09-19.
 */
import Format from 'app/src/data/Format';
import RetailModel from 'app/src/data/models/RetailModel';
import URLHelper from 'app/src/data/models/utils/URLHelper';

const PaymentTypeTypes = {
  GIFT_CARD: 'gift card',
  CASH: 'cash',
  CREDIT_CARD: 'credit card',
  CREDIT_ACCOUNT: 'credit account',
  USER_DEFINED: 'user defined',
  ECOM: 'ecom',
  ADJUSTMENT: 'adjustment',
};

// Should match: pos/payments/PaymentTypeCode
const PaymentTypeCodes = {
  CASH: 'Cash',
  CHECK: 'Check',
  CREDIT_CARD: 'Credit Card',
  CREDIT_ACCOUNT: 'SCA',
  GIFT_CARD: 'SGC',
  DEBIT_CARD: 'Debit Card',
};

const RefundOptions = {
  CREDIT_ACCOUNT_OR_GIFTCARD_ONLY: 'no_original_receipt_refund_to_credit_account_only',
  DISABLE_CREDIT_ACCOUNT_REFUNDS: 'disable_credit_refunds',
  DISABLE_GIFTCARD_REFUNDS: 'disable_giftcard_refunds',
};

export default class PaymentType extends RetailModel {
  static get Types() {
    return PaymentTypeTypes;
  }

  static get Refunds() {
    return RefundOptions;
  }

  static get Codes() {
    return PaymentTypeCodes;
  }

  // Server uses the saleID field as the model's unique identifier. This
  // informs the Backbone.Model of this fact.
  get idAttribute() {
    return 'paymentTypeID';
  }

  get defaults() {
    return {
      name: '',
      requireCustomer: false,
      archived: false,
      code: '',
      internalReserved: false,
      type: '',
      refundAsPaymentTypeID: 0,
      channel: '',
    };
  }

  get retailResponseName() {
    return 'PaymentType';
  }

  /**
   * Return the url for the PaymentType endpoint
   */
  url() {
    let URL = URLHelper.getAccountURL('/PaymentType');
    if (!this.isNew()) {
      URL += `/${this.id}`;
    }
    return `${URL}.json`;
  }

  /**
   * Overridden to parse out the superfluous json that the LSCloud returns us
   * @param response - the json object returned from the fetch call before this object has initialized from it
   * @returns {*} the json object that this object will initialize itself from
   */
  parse(response) {
    let parsedResponse = super.parse(response);

    // the Cloud API has a tendency to send all values as strings so let's make sure they're of the proper type
    parsedResponse.paymentTypeID = Format.parseInt(parsedResponse.paymentTypeID);
    parsedResponse.requireCustomer = Format.parseBoolean(parsedResponse.requireCustomer);
    parsedResponse.archived = Format.parseBoolean(parsedResponse.archived);
    parsedResponse.internalReserved = Format.parseBoolean(parsedResponse.internalReserved);
    parsedResponse.refundAsPaymentTypeID = Format.parseInt(parsedResponse.refundAsPaymentTypeID);

    return parsedResponse;
  }

  /**
   * Determines if the payment is a cash payment
   *
   * @returns {boolean}   True if the payment is a cash payment
   */
  isCash() {
    return this.get('type') === PaymentTypeTypes.CASH;
  }

  /**
   * Determines if the payment is a credit card payment
   *
   * @returns {boolean}   True if the payment is a credit card payment
   */
  isGiftCard() {
    return this.get('type') === PaymentTypeTypes.GIFT_CARD;
  }
}
